import * as fmt from './format';

/**
 * Rendering a value the app has never heard of.
 *
 * The column picker offers whatever the ingest pipeline discovered, which
 * means the grid has to display attributes nobody wrote a formatter for. The
 * catalog carries a type and a unit, and that is enough: numbers get sensible
 * precision and their unit, booleans read as words rather than true/false, and
 * strings are shown as they arrived.
 *
 * Anything with a hand-written formatter — speed, temperature, signal — keeps
 * it. Discovery is the fallback, not a replacement for knowing better.
 */

const KNOWN_FORMATTERS = {
  speed_kph: (v) => fmt.speed(v),
  temp_c: (v) => fmt.temperature(v),
  humidity_pct: (v) => fmt.percent(v, 1),
  mem_pct: (v) => fmt.percent(v, 1),
  heading_deg: (v) => `${Math.round(v)}°`,
  gsm_signal: (v) => `${v}/31`,
  altitude_m: (v) => `${Math.round(v)} m`,
  health_score: (v) => String(Math.round(v)),
};

/** Booleans read better as the thing they mean than as true/false. */
const BOOLEAN_WORDS = {
  door_open: ['Open', 'Closed'],
  ignition: ['On', 'Off'],
  compressor_on: ['Running', 'Off'],
  tilt_alert: ['Alert', 'Clear'],
  position_valid: ['Valid', 'No fix'],
};

export function formatAttribute(attribute, value) {
  if (value === null || value === undefined || value === '') return '—';

  const known = KNOWN_FORMATTERS[attribute.key];
  if (known) {
    try {
      return known(value);
    } catch {
      /* fall through to the generic path */
    }
  }

  if (attribute.type === 'boolean' || typeof value === 'boolean') {
    const words = BOOLEAN_WORDS[attribute.key] || ['Yes', 'No'];
    return value ? words[0] : words[1];
  }

  if (attribute.type === 'number' || typeof value === 'number') {
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value);

    // Show the precision the device actually sent, capped at two decimals.
    // Padding 36.8 into "36.80 %" invents accuracy that is not in the reading;
    // rounding 13.17 V to "13 V" throws away accuracy that is. Above a
    // thousand the decimals stop being read at all, so an odometer is whole
    // kilometres.
    const sent = (String(value).split('.')[1] || '').length;
    const decimals = Math.abs(n) >= 1000 ? 0 : Math.min(2, sent);

    const formatted = fmt.number(n, decimals);
    return attribute.unit ? `${formatted} ${attribute.unit}` : formatted;
  }

  return String(value);
}

/**
 * Should this value be flagged? A door left open or a tamper alert is worth
 * colouring; a fuel level is not. Only booleans whose "true" is a problem.
 */
const ATTENTION_WHEN_TRUE = new Set(['door_open', 'tilt_alert']);

export function attributeTone(attribute, value) {
  if (attribute.type !== 'boolean' && typeof value !== 'boolean') return null;
  if (!ATTENTION_WHEN_TRUE.has(attribute.key)) return null;
  return value ? 'warning' : null;
}

/** Right-align numbers so a column of them lines up; left-align the rest. */
export function attributeAlign(attribute) {
  return attribute.type === 'number' ? 'right' : 'left';
}

/**
 * Read an attribute off a vehicle row.
 *
 * Core attributes are also promoted to real columns on the row (`last_speed_kph`
 * and friends), but `attributes` holds every one of them under its canonical
 * key, so one lookup covers core and discovered alike.
 */
export function readAttribute(row, key) {
  const map = row?.attributes;
  if (map && Object.prototype.hasOwnProperty.call(map, key)) return map[key];
  return row?.[key] ?? null;
}

/**
 * The columns a person sees before they have chosen anything.
 *
 * Device health is a fixed column on the grid already, so it is deliberately
 * not here — the default view should not open with the same number twice.
 */
export const DEFAULT_COLUMNS = ['speed_kph', 'gsm_signal'];

/**
 * A readable name for a key the catalog has not described yet — a device can
 * always report something one packet before the catalog registers it, and
 * `reefer_temp_c` is a worse label than "Reefer temp c" but a much worse one
 * than showing nothing.
 */
export function humanKeyFallback(key) {
  return String(key)
    .replace(/_/g, ' ')
    .trim()
    .replace(/^./, (character) => character.toUpperCase());
}
